import { useMemo } from 'react'
import useSWR from 'swr'
import { cadinuProfileAbi } from 'config/abi/cadinuProfile'
import { CadinuLevelNftsAbi } from 'config/abi/cadinuLevelNfts'
import { localNftImage } from 'state/profile/localNftImage'
import { getCadinuProfileAddress } from 'utils/addressHelpers'
import { publicClient } from 'utils/wagmi'
import { zeroAddress } from 'viem'
import { PROFILE_CHAIN_ID } from './chainTx'

/** Minimal ABIs of the calls the profile flows make besides CadinuProfile (same calls as the legacy pages). */
export const ERC721_APPROVE_ABI = [
  { type: 'function', name: 'approve', stateMutability: 'nonpayable', inputs: [{ name: 'to', type: 'address' }, { name: 'tokenId', type: 'uint256' }], outputs: [] },
  { type: 'function', name: 'getApproved', stateMutability: 'view', inputs: [{ name: 'tokenId', type: 'uint256' }], outputs: [{ type: 'address' }] },
  { type: 'function', name: 'isApprovedForAll', stateMutability: 'view', inputs: [{ name: 'owner', type: 'address' }, { name: 'operator', type: 'address' }], outputs: [{ type: 'bool' }] },
] as const
export const ERC20_ABI = [
  { type: 'function', name: 'approve', stateMutability: 'nonpayable', inputs: [{ name: 'spender', type: 'address' }, { name: 'amount', type: 'uint256' }], outputs: [{ type: 'bool' }] },
  { type: 'function', name: 'allowance', stateMutability: 'view', inputs: [{ name: 'owner', type: 'address' }, { name: 'spender', type: 'address' }], outputs: [{ type: 'uint256' }] },
  { type: 'function', name: 'balanceOf', stateMutability: 'view', inputs: [{ name: 'a', type: 'address' }], outputs: [{ type: 'uint256' }] },
] as const

const client = () => publicClient({ chainId: PROFILE_CHAIN_ID })
const PROFILE = () => getCadinuProfileAddress()

/** The character and our own image of an Identity Arts collection (state/profile/localNftImage). */
export function collectionInfo(collection: string) {
  const image = localNftImage(collection)
  const m = image?.match(/level(\d+)\/cadinu_nft_([a-z]+)_/)
  return { image, level: m ? Number(m[1]) : null, character: m ? m[2] : 'NFT' }
}

export interface OnchainProfile {
  registered: boolean
  active: boolean
  points: bigint
  level: number
  collection: `0x${string}` | null
  tokenId: bigint
}

/** The wallet's CadinuProfile: isRegistered + getUserProfile (userId, points, level, nftAddress, tokenId, isActive). */
export function useOnchainProfile(wallet?: string | null) {
  const { data, error, mutate, isLoading } = useSWR<OnchainProfile>(
    wallet ? ['cadinu-onchain-profile', wallet.toLowerCase()] : null,
    async () => {
      // getUserProfile reverts (__errorUserNotRegistered) for a wallet without a profile: only isRegistered must answer
      const [reg, prof] = await client().multicall({
        allowFailure: true,
        contracts: [
          { address: PROFILE(), abi: cadinuProfileAbi, functionName: 'isRegistered', args: [wallet as `0x${string}`] },
          { address: PROFILE(), abi: cadinuProfileAbi, functionName: 'getUserProfile', args: [wallet as `0x${string}`] },
        ],
      })
      if (reg.status !== 'success') throw reg.error
      const registered = Boolean(reg.result)
      if (!registered) return { registered: false, active: false, points: 0n, level: 0, collection: null, tokenId: 0n }
      if (prof.status !== 'success') throw prof.error
      const p = prof.result as unknown as readonly [bigint, bigint, bigint, `0x${string}`, bigint, boolean]
      return {
        registered: true,
        active: Boolean(p[5]),
        points: p[1],
        level: Number(p[2]),
        collection: p[3] && p[3] !== zeroAddress ? p[3] : null,
        tokenId: p[4],
      }
    },
    { revalidateOnFocus: false, shouldRetryOnError: true, errorRetryCount: 2 },
  )
  return { profile: data, error, loading: isLoading, refresh: mutate }
}

export interface HeldNft {
  collection: `0x${string}`
  level: number
  character: string
  image: string | null
  tokenIds: bigint[]
}

/** Every CADINU Identity Arts NFT the wallet holds, grouped by collection, read from BNB Chain (the profile contract's level lists, then tokensOfOwner). */
export function useHeldCadinuNfts(wallet?: string | null) {
  const { data, error, mutate, isLoading } = useSWR<HeldNft[]>(
    wallet ? ['cadinu-held-nfts', wallet.toLowerCase()] : null,
    async () => {
      const levels = Number(await client().readContract({ address: PROFILE(), abi: cadinuProfileAbi, functionName: 'numberOfLevels' }))
      const lists = await client().multicall({
        allowFailure: false,
        contracts: Array.from({ length: levels }, (_, i) => ({ address: PROFILE(), abi: cadinuProfileAbi, functionName: 'getNftAddressesForLevel', args: [BigInt(i + 1)] }) as const),
      })
      const cols = lists.flatMap((l, i) => (l as readonly `0x${string}`[]).map((c) => ({ c, level: i + 1 })))
      const held = await client().multicall({
        allowFailure: true,
        contracts: cols.map(({ c }) => ({ address: c, abi: CadinuLevelNftsAbi, functionName: 'tokensOfOwner', args: [wallet as `0x${string}`] }) as const),
      })
      return cols
        .map(({ c, level }, i) => {
          const ids = ((held[i]?.result as readonly bigint[] | undefined) ?? []).slice().sort((a, b) => (a < b ? -1 : 1))
          const info = collectionInfo(c)
          return { collection: c, level, character: info.character, image: info.image, tokenIds: ids }
        })
        .filter((x) => x.tokenIds.length > 0)
    },
    { revalidateOnFocus: false },
  )
  const total = useMemo(() => (data ?? []).reduce((n, x) => n + x.tokenIds.length, 0), [data])
  return { nfts: data, total, error, loading: isLoading, refresh: mutate }
}

/** Is this NFT approved for the profile contract (the legacy check: getApproved(tokenId) == CadinuProfile, or operator approval)? */
export async function nftApprovedForProfile(collection: `0x${string}`, tokenId: bigint, owner: `0x${string}`) {
  const [one, all] = await client().multicall({
    allowFailure: true,
    contracts: [
      { address: collection, abi: ERC721_APPROVE_ABI, functionName: 'getApproved', args: [tokenId] },
      { address: collection, abi: ERC721_APPROVE_ABI, functionName: 'isApprovedForAll', args: [owner, PROFILE()] },
    ],
  })
  return (typeof one.result === 'string' && one.result.toLowerCase() === PROFILE().toLowerCase()) || all.result === true
}
